/**
 * ContactPickerModal component.
 * @module components/Widgets/ContactWidget/ContactPickerModal
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  Confirm,
  Input,
  Modal,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { toast } from 'react-toastify';

import { Icon, Toast } from '@plone/volto/components';
import { Form } from '@plone/volto/components/manage/Form';
import { createContent, getContent } from '@plone/volto/actions';
import { setFormData } from '@plone/volto/actions/form/form';
import { nestContent } from '@plone/volto/helpers/Content/Content';
import Api from '@plone/volto/helpers/Api/Api';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import addSVG from '@plone/volto/icons/add.svg';
import checkSVG from '@plone/volto/icons/check.svg';
import pencilSVG from '@plone/volto/icons/pencil.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';

const LIST_SUBREQUEST = 'contact-picker-list';
const ADD_SUBREQUEST = 'contact-picker-add-contact';
const CONTACTS_PATH = '/contacts';

/**
 * ContactPickerModal
 * Lets the user search for and select an existing contact, or add a new
 * one, from within a modal (used by ContactWidget).
 */
const ContactPickerModal = ({
  open,
  onClose: onCloseModal,
  onSelect: onSelectContact,
  selectedId,
}) => {
  const dispatch = useDispatch();
  const store = useStore();

  // The add/edit contact forms below are `global` forms (so widgets like the
  // state widget can read the contact's country), which overwrites the shared
  // state.form.global slot the outer form (e.g. the Order) lives in. Snapshot
  // the outer form data when the modal opens and restore it when we're done.
  const outerFormData = useRef(null);
  const restoreOuterFormData = () => {
    if (outerFormData.current) {
      dispatch(setFormData(outerFormData.current));
    }
  };

  const onClose = () => {
    restoreOuterFormData();
    onCloseModal();
  };

  // Restore the outer form data first, then let the outer form handle the
  // selection once it has re-synced.
  const onSelect = (id) => {
    restoreOuterFormData();
    setTimeout(() => onSelectContact(id), 0);
  };

  const [mode, setMode] = useState('list');
  const [editingContact, setEditingContact] = useState(null);
  const formRef = useRef();

  // Contact schema, fetched directly from the API (bypassing the
  // getSchema action) so it doesn't clobber the global schema.schema
  // slot that an outer Add/Edit form (e.g. for an Order) may be relying
  // on while this modal is open.
  const [contactSchema, setContactSchema] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Filtering
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');

  // Sorting
  const [sort, setSort] = useState('last_name');
  const [dir, setDir] = useState('asc');

  const backToList = () => {
    restoreOuterFormData();
    setMode('list');
    setEditingContact(null);
  };

  useEffect(() => {
    if (open) {
      outerFormData.current = store.getState().form?.global || null;
      backToList();
      dispatch(getContent(CONTACTS_PATH, null, LIST_SUBREQUEST));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dispatch]);

  useEffect(() => {
    if ((mode === 'add' || mode === 'edit') && !contactSchema) {
      const api = new Api();
      api
        .get(`${CONTACTS_PATH}/@types/Contact`)
        .then((schema) => setContactSchema(schema))
        .catch(() => {
          toast.error(
            <Toast
              error
              title="Error"
              content="Unable to load the contact form."
            />,
          );
        });
    }
  }, [mode, contactSchema]);

  const listRequest = useSelector(
    (state) => state.content.subrequests?.[LIST_SUBREQUEST],
  );
  const addRequest = useSelector(
    (state) => state.content.subrequests?.[ADD_SUBREQUEST],
  );

  const wasAdding = useRef(false);
  useEffect(() => {
    if (wasAdding.current && addRequest?.loaded) {
      // Select the newly added contact and close the modal.
      const newId =
        addRequest.data?.id ||
        String(addRequest.data?.['@id'] || '')
          .split('/')
          .pop();
      backToList();
      if (newId) {
        onSelect(newId);
      } else {
        restoreOuterFormData();
        dispatch(getContent(CONTACTS_PATH, null, LIST_SUBREQUEST));
      }
    }
    if (wasAdding.current && addRequest?.error) {
      toast.error(
        <Toast
          error
          title="Error"
          content={
            addRequest.error?.response?.body?.message ||
            'Unable to add contact.'
          }
        />,
      );
    }
    wasAdding.current = !!addRequest?.loading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRequest?.loading, addRequest?.loaded, addRequest?.error, dispatch]);

  const filterField = (items, field, value) =>
    value === ''
      ? items
      : items.filter(
          (item) =>
            item[field] &&
            item[field].toLowerCase().indexOf(value.toLowerCase()) !== -1,
        );

  let items = listRequest?.data?.items || [];
  items = filterField(items, 'first_name', firstName);
  items = filterField(items, 'last_name', lastName);
  items = filterField(items, 'company', company);
  items = filterField(items, 'email', email);
  items = [...items].sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  const onAddSubmit = (data) => {
    dispatch(
      createContent(
        CONTACTS_PATH,
        {
          ...data,
          '@static_behaviors': contactSchema?.definitions
            ? Object.keys(contactSchema.definitions)
            : null,
          '@type': 'Contact',
        },
        ADD_SUBREQUEST,
      ),
    );
  };

  const onEditClick = (event, item) => {
    event.stopPropagation();
    setEditingContact(item);
    setMode('edit');
  };

  // Update via a raw API call (bypassing the updateContent action) so it
  // doesn't touch the global state.content.update slot an outer Edit form
  // (e.g. for an Order) may be relying on to know when its own save has
  // finished.
  const onEditSubmit = (data) => {
    setEditSaving(true);
    const api = new Api();
    api
      .patch(`${CONTACTS_PATH}/${editingContact.id}`, {
        data: nestContent(data),
      })
      .then(() => {
        setEditSaving(false);
        backToList();
        dispatch(getContent(CONTACTS_PATH, null, LIST_SUBREQUEST));
      })
      .catch((error) => {
        setEditSaving(false);
        toast.error(
          <Toast
            error
            title="Error"
            content={
              error?.response?.body?.message || 'Unable to save contact.'
            }
          />,
        );
      });
  };

  // Delete via a raw API call, for the same reason the edit save above
  // avoids the deleteContent action: it writes to a global, non-keyed
  // state.content.delete slot regardless of subrequest.
  const onDelete = () => {
    setDeleting(true);
    const api = new Api();
    api
      .del(`${CONTACTS_PATH}/${editingContact.id}`)
      .then(() => {
        setDeleting(false);
        setShowDeleteConfirm(false);
        backToList();
        dispatch(getContent(CONTACTS_PATH, null, LIST_SUBREQUEST));
      })
      .catch((error) => {
        setDeleting(false);
        setShowDeleteConfirm(false);
        toast.error(
          <Toast
            error
            title="Error"
            content={
              error?.response?.body?.message || 'Unable to delete contact.'
            }
          />,
        );
      });
  };

  return (
    <Modal
      size="large"
      open={open}
      onClose={onClose}
      className="contact-picker-modal"
    >
      <Modal.Header className="contact-picker-header">
        <div className="contact-picker-header-action">
          {mode === 'list' ? (
            <button
              type="button"
              className="item ui noborder button primary"
              onClick={() => setMode('add')}
              aria-label="Add contact"
            >
              <Icon name={addSVG} size="20px" />
            </button>
          ) : (
            <button
              type="button"
              className="item ui noborder button primary"
              disabled={!!addRequest?.loading || editSaving || !contactSchema}
              onClick={() => formRef.current?.onSubmit()}
              aria-label="Save"
            >
              <Icon name={checkSVG} size="20px" />
            </button>
          )}
        </div>
        <div className="contact-picker-header-title">
          {mode === 'list' && 'Select a contact'}
          {mode === 'add' && 'Add contact'}
          {mode === 'edit' && 'Edit contact'}
        </div>
        <div className="contact-picker-header-action">
          {mode === 'edit' && (
            <button
              type="button"
              className="item ui noborder button"
              disabled={deleting}
              onClick={() => setShowDeleteConfirm(true)}
              aria-label="Delete contact"
            >
              <Icon name={deleteSVG} size="20px" />
            </button>
          )}
          <button
            type="button"
            className="item ui noborder button"
            onClick={mode === 'list' ? onClose : backToList}
            aria-label="Close"
          >
            <Icon name={clearSVG} size="20px" />
          </button>
        </div>
      </Modal.Header>
      <Confirm
        className="contact-delete-confirm"
        open={showDeleteConfirm}
        header="Delete contact"
        content={`Are you sure you want to delete "${editingContact?.title || editingContact?.first_name || ''}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <Modal.Content>
        {mode === 'list' ? (
          <div className="scrollable-table">
            <Table striped celled selectable>
              <TableHeader>
                <TableRow className="tools-header">
                  <TableHeaderCell
                    onClick={() => onSort('first_name')}
                    width="2"
                  >
                    First Name {sort === 'first_name' && <SortIcon />}
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => onSort('last_name')}
                    width="2"
                  >
                    Last Name {sort === 'last_name' && <SortIcon />}
                  </TableHeaderCell>
                  <TableHeaderCell onClick={() => onSort('company')} width="4">
                    Company {sort === 'company' && <SortIcon />}
                  </TableHeaderCell>
                  <TableHeaderCell onClick={() => onSort('email')} width="4">
                    Email {sort === 'email' && <SortIcon />}
                  </TableHeaderCell>
                  <TableHeaderCell width="1" />
                </TableRow>
                <TableRow className="tools-filter">
                  <TableHeaderCell>
                    <div className="filter-input">
                      <Input
                        value={firstName}
                        placeholder="Filter first name"
                        autoCorrect="off"
                        spellCheck="false"
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                      {firstName && (
                        <Icon
                          name={clearSVG}
                          size="16px"
                          className="clear-icon"
                          onClick={() => setFirstName('')}
                        />
                      )}
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <div className="filter-input">
                      <Input
                        value={lastName}
                        placeholder="Filter last name"
                        autoCorrect="off"
                        spellCheck="false"
                        onChange={(e) => setLastName(e.target.value)}
                      />
                      {lastName && (
                        <Icon
                          name={clearSVG}
                          size="16px"
                          className="clear-icon"
                          onClick={() => setLastName('')}
                        />
                      )}
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <div className="filter-input">
                      <Input
                        value={company}
                        placeholder="Filter company"
                        autoCorrect="off"
                        spellCheck="false"
                        onChange={(e) => setCompany(e.target.value)}
                      />
                      {company && (
                        <Icon
                          name={clearSVG}
                          size="16px"
                          className="clear-icon"
                          onClick={() => setCompany('')}
                        />
                      )}
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <div className="filter-input">
                      <Input
                        value={email}
                        placeholder="Filter email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      {email && (
                        <Icon
                          name={clearSVG}
                          size="16px"
                          className="clear-icon"
                          onClick={() => setEmail('')}
                        />
                      )}
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    className={`clickable-row${
                      selectedId && item.id === selectedId
                        ? ' selected-row'
                        : ''
                    }`}
                    onClick={() => onSelect(item.id)}
                  >
                    <TableCell>{item.first_name}</TableCell>
                    <TableCell>{item.last_name}</TableCell>
                    <TableCell>{item.company}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell textAlign="center" className="edit-cell">
                      {item.id !== '000000' && (
                        <button
                          type="button"
                          className="circular-button"
                          onClick={(event) => onEditClick(event, item)}
                          aria-label="Edit contact"
                        >
                          <Icon name={pencilSVG} size="18px" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          contactSchema &&
          (mode === 'edit' ? (
            <div className="contact-add">
              <Form
                isEditForm
                ref={formRef}
                schema={contactSchema}
                formData={editingContact}
                onSubmit={onEditSubmit}
                hideActions
                pathname={CONTACTS_PATH}
                loading={editSaving}
                global
              />
            </div>
          ) : (
            <div className="contact-add">
              <Form
                ref={formRef}
                schema={contactSchema}
                onSubmit={onAddSubmit}
                hideActions
                pathname={CONTACTS_PATH}
                loading={!!addRequest?.loading}
                global
              />
            </div>
          ))
        )}
      </Modal.Content>
    </Modal>
  );
};

ContactPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
};

export default ContactPickerModal;
